import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'

/**
 * Reads the stored partner-network health scan.
 *
 * These endpoints never run a scan - a full pass takes seconds over a large
 * book, so results are stored and the scan runs on a schedule. That is also
 * why the query keys sit in the COUNTS cache profile rather than LIST_LIVE:
 * the data only changes when a scan completes, so refetching on every mount
 * would be pure cost.
 *
 * ``api`` unwraps the APIResponse envelope in a response interceptor
 * (``unwrapApiEnvelope``), so ``r.data`` is already the payload - reading
 * ``r.data.result`` here yields undefined and the panel renders as empty.
 */

export interface PartnershipHealthCheckCount {
  key: string
  label: string
  count: number
}

export interface PartnershipHealthScan {
  uuid: string
  status: string
  started_at: string
  finished_at: string | null
  partnerships_scanned: number
  checks: PartnershipHealthCheckCount[]
}

export interface PartnershipHealthSummary {
  scan: PartnershipHealthScan | null
  /** Whether the network is small enough for an on-demand scan. Server-decided. */
  can_rescan?: boolean
  checks: PartnershipHealthCheckCount[]
}

export interface PartnershipHealthFinding {
  uuid: string
  check_key: string
  check_label: string
  partnership_uuid: string | null
  organisation_uuid: string | null
  name: string
  group_key: string
  details: Record<string, unknown>
}

export interface DuplicateGroupMember {
  partnership_uuid: string | null
  name?: string
  account_number?: string
  email_domain?: string
  registration_count?: number
  user_count?: number
}

export interface DuplicateGroup {
  key: string
  display_name: string
  bucket: 'clear' | 'needs_review' | 'blocked' | string
  record_count: number
  registration_count: number
  account_numbers: string[]
  email_domains: string[]
  parents_seen: string[]
  flags: string[]
  suggested_survivor_uuid: string | null
  members: DuplicateGroupMember[]
}

export function usePartnershipHealth() {
  const { orgUuid } = useOrg()
  return useQuery<PartnershipHealthSummary>({
    queryKey: ['partnership-health', orgUuid],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/partnership-health/`)
        .then((r) => r.data),
    enabled: !!orgUuid,
  })
}

/**
 * One page of findings for a check.
 *
 * Paged rather than capped: a check can hold thousands of records (7,576 for
 * "nobody can sign in" on a migrated book), and a fixed first-100 slice makes
 * the rest unreachable - the operator cannot work through what they cannot
 * open.
 */
export function usePartnershipHealthFindings(
  checkKey: string | null,
  page = 1,
  pageSize = 25,
) {
  const { orgUuid } = useOrg()
  return useQuery<{ results: PartnershipHealthFinding[]; count: number }>({
    queryKey: ['partnership-health-findings', orgUuid, checkKey, page, pageSize],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/partnership-health/findings/`, {
          params: {
            ...(checkKey ? { check_key: checkKey } : {}),
            page: String(page),
            page_size: String(pageSize),
          },
        })
        .then((r) => r.data),
    enabled: !!orgUuid && !!checkKey,
    placeholderData: (prev) => prev,
  })
}

export function usePartnershipDuplicates() {
  const { orgUuid } = useOrg()
  return useQuery<{ groups: DuplicateGroup[] }>({
    queryKey: ['partnership-health-duplicates', orgUuid],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/partnership-health/duplicates/`)
        .then((r) => r.data),
    enabled: !!orgUuid,
  })
}




/**
 * Run the scan now.
 *
 * **Not wired to any control yet** - the "Check again" button is commented out
 * in `network-attention-panel.tsx` until scanning is a background task, because
 * a synchronous full pass is the wrong shape for a book the size OC will carry.
 * Kept so it can be restored with the button.
 *
 * The server rate-limits to one scan per organisation per minute and refuses a
 * book above its size ceiling.
 */
export function useRescanPartnershipHealth() {
  const { orgUuid } = useOrg()
  const queryClient = useQueryClient()
  return useMutation<{ ran: boolean }, unknown, void>({
    mutationFn: () =>
      api
        .post(`/organisations/${orgUuid}/partnership-health/rescan/`)
        .then((r) => r.data?.result ?? r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partnership-health'] })
      queryClient.invalidateQueries({ queryKey: ['partnership-health-findings'] })
      queryClient.invalidateQueries({ queryKey: ['partnership-health-duplicates'] })
      queryClient.invalidateQueries({ queryKey: ['partnership-duplicates'] })
    },
  })
}
